import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, memo } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage, FormattedDate } from 'react-intl'
import { GroupedVirtuoso } from 'react-virtuoso'
import unionBy from 'lodash/unionBy'
import groupBy from 'lodash/groupBy'
import isEqual from 'lodash/isEqual'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import { useTeam } from '../../../contexts/TeamContext'
import { useServices } from '../../../contexts/ServicesContext'
import { useProjects } from '../../../contexts/ProjectsContext'
import useServiceLogsMessagesResources from '../../../shared/resources/api/v1/admin/docker/useServiceLogsMessagesResources'
import LoadingState from '../../../shared/components/LoadingState'
import ErrorState from '../../../shared/components/ErrorState'
import PageBreadcrumbs from '../../../shared/components/PageBreadcrumbs'
import LogFilters from './components/LogFilters'
import useLogFilters from './hooks/useLogFilters'

const SERVICE_COLORS = [
  '#25c2a0', '#e06c75', '#d19a66', '#61afef', '#c678dd',
  '#56b6c2', '#98c379', '#e5c07b', '#be5046', '#7ec8e3',
]

const getServiceColor = (name) => {
  const hash = (name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return SERVICE_COLORS[hash % SERVICE_COLORS.length]
}

const formatServicePrefix = (name) => {
  const truncated = (name || '').slice(0, 14)
  return truncated.padEnd(14, ' ') + ' | '
}

// Memoized log row component to prevent re-renders during scroll
const LogRow = memo(function LogRow({ log, service, onServiceClick }) {
  const prefix = useMemo(() => formatServicePrefix(service?.name), [service?.name])
  const color = useMemo(() => getServiceColor(service?.name), [service?.name])

  const handleClick = useCallback(() => {
    if (service) onServiceClick(service.id)
  }, [service, onServiceClick])

  return (
    <Box
      sx={{
        px: 1,
        fontFamily: 'monospace',
        fontSize: '0.825rem',
        lineHeight: 1.1,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      <Typography
        component="span"
        onClick={handleClick}
        sx={{
          fontFamily: 'inherit',
          fontSize: 'inherit',
          lineHeight: 'inherit',
          color,
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {prefix}
      </Typography>
      {log.message}
    </Box>
  )
})

// Extracted components for GroupedVirtuoso (stable references)
const TopItemList = forwardRef(function TopItemList({ children, ...props }, ref) {
  return (
    <Box {...props} ref={ref} sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
      {children}
    </Box>
  )
})

const Footer = memo(function Footer({ context }) {
  if (!context?.loadingMore) return null
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
      <CircularProgress size={24} />
    </Box>
  )
})

// Stable components object for GroupedVirtuoso
const virtuosoComponents = {
  TopItemList,
  Footer,
}

const INITIAL_LIMIT = 100
const LOAD_MORE_LIMIT = 50
const MAX_LOGS = 5000

const getDateKey = (log) => new Date(log.loggedAt).toISOString().split('T')[0]

function LogsIndexPage() {
  const { teamSlug } = useParams()
  const { team } = useTeam()
  const { servicesById, loading: servicesLoading } = useServices()
  const { projectsById, loading: projectsLoading } = useProjects()
  const { all: allLogs } = useServiceLogsMessagesResources(teamSlug)
  const { filters, setFilters, apiParams } = useLogFilters()

  const handleServiceClick = useCallback((serviceId) => {
    setFilters({ projectIds: [], serviceIds: [serviceId], severities: [] })
  }, [setFilters])
  const prevApiParamsRef = useRef(apiParams)
  const apiParamsRef = useRef(apiParams)
  apiParamsRef.current = apiParams
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [initialLoading, setInitialLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [meta, setMeta] = useState({ total: 0, limit: INITIAL_LIMIT, offset: 0 })
  const [isAtBottom, setIsAtBottom] = useState(false)

  const hasMore = meta.offset + meta.limit < meta.total

  // Compute groups for GroupedVirtuoso (newest dates first)
  const { groups, groupCounts } = useMemo(() => {
    const grouped = groupBy(logs, (log) => getDateKey(log))
    const sortedDates = Object.keys(grouped).sort().reverse()
    return {
      groups: sortedDates,
      groupCounts: sortedDates.map((date) => grouped[date].length)
    }
  }, [logs])

  // Precompute log data with resolved service/project to avoid lookups during scroll
  const enrichedLogs = useMemo(() =>
    logs.map(log => {
      const service = servicesById[log.serviceId]
      const project = service ? projectsById[service.dockerProjectId] : null
      return { log, service, project }
    })
  , [logs, servicesById, projectsById])

  // Memoized group content renderer
  const renderGroupContent = useCallback((index) => (
    <Box
      sx={{
        bgcolor: 'background.paper',
        py: 1,
        px: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold">
        <FormattedDate
          value={groups[index]}
          year="numeric"
          month="long"
          day="numeric"
          weekday="long"
        />
      </Typography>
    </Box>
  ), [groups])

  const refreshLogs = useCallback(() => {
    allLogs({ params: { limit: LOAD_MORE_LIMIT, ...apiParamsRef.current } })
      .then((logsResponse) => {
        // New logs appear at top, unionBy deduplicates by id
        const newLogs = logsResponse.serviceLogMessages || []
        setLogs((prevLogs) => {
          const merged = unionBy(newLogs, prevLogs, 'id')
          return merged.length > MAX_LOGS ? merged.slice(0, MAX_LOGS) : merged
        })
        if (logsResponse.meta) {
          setMeta((prevMeta) => ({ ...prevMeta, total: logsResponse.meta.total }))
        }
      })
      .catch(() => {
        // Silently ignore refresh errors
      })
  }, [allLogs])

  // Store latest refreshLogs in ref to avoid interval reset on every render
  const refreshLogsRef = useRef(refreshLogs)
  refreshLogsRef.current = refreshLogs

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return

    setLoadingMore(true)
    allLogs({ params: { limit: LOAD_MORE_LIMIT, offset: logs.length, ...apiParams } })
      .then((logsResponse) => {
        // Older logs append to bottom
        const olderLogs = logsResponse.serviceLogMessages || []
        setLogs((prevLogs) => {
          const merged = [...prevLogs, ...olderLogs]
          return merged.length > MAX_LOGS ? merged.slice(0, MAX_LOGS) : merged
        })
        if (logsResponse.meta) {
          setMeta(logsResponse.meta)
        }
      })
      .catch(() => {
        // Silently ignore load more errors
      })
      .finally(() => {
        setLoadingMore(false)
      })
  }, [allLogs, loadingMore, hasMore, logs.length, apiParams])

  useEffect(() => {
    // Wait for contexts to load
    if (servicesLoading || projectsLoading) return

    // Check if filters changed - if so, reset logs
    const filtersChanged = !isEqual(prevApiParamsRef.current, apiParams)
    if (filtersChanged) {
      setLogs([])
      setMeta({ total: 0, limit: INITIAL_LIMIT, offset: 0 })
      prevApiParamsRef.current = apiParams
    }

    setLoading(true)
    setError(null)

    allLogs({ params: { limit: INITIAL_LIMIT, ...apiParams } })
      .then((logsResponse) => {
        // Display order: newest first, oldest last
        setLogs(logsResponse.serviceLogMessages || [])
        if (logsResponse.meta) {
          setMeta(logsResponse.meta)
        }
      })
      .catch((err) => {
        setError(err)
      })
      .finally(() => {
        setLoading(false)
        setInitialLoading(false)
      })
  }, [teamSlug, servicesLoading, projectsLoading, apiParams])

  useEffect(() => {
    const interval = setInterval(() => refreshLogsRef.current(), 10000)
    return () => clearInterval(interval)
  }, [])

  // Trigger loadMore when user scrolls to bottom
  useEffect(() => {
    if (isAtBottom && !loadingMore && hasMore) {
      setIsAtBottom(false)  // Reset so we don't loop
      loadMore()
    }
  }, [isAtBottom, loadingMore, hasMore, loadMore])

  if (initialLoading || servicesLoading || projectsLoading) return <LoadingState />
  if (error) return <ErrorState messageId="dockerLogs.error" />

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sticky Header */}
      <Box sx={{ flexShrink: 0, pt: 4, pb: 2, px: 3, borderBottom: 1, borderColor: 'divider', boxShadow: 1 }}>
        <PageBreadcrumbs entities={{ team }} />
        <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
          <FormattedMessage id="dockerLogs.title" />
        </Typography>
        <LogFilters
          filters={filters}
          onChange={setFilters}
        />
      </Box>

      {/* Virtualized Logs Panel */}
      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : logs.length === 0 ? (
        <Box sx={{ flex: 1, px: 3, py: 2 }}>
          <Typography color="text.secondary">
            <FormattedMessage id="dockerLogs.empty" />
          </Typography>
        </Box>
      ) : (
        <GroupedVirtuoso
          style={{ flex: 1, minHeight: 0 }}
          groupCounts={groupCounts}
          atBottomStateChange={setIsAtBottom}
          increaseViewportBy={{ top: 200, bottom: 200 }}
          context={{ loadingMore }}
          components={virtuosoComponents}
          groupContent={renderGroupContent}
          itemContent={(index) => {
            const { log, service } = enrichedLogs[index]
            return (
              <LogRow
                log={log}
                service={service}
                onServiceClick={handleServiceClick}
              />
            )
          }}
        />
      )}
    </Box>
  )
}

export default LogsIndexPage
